"use client";

import useSimulatedProgress from "@/hooks/useSimulatedProgress";

/**
 * La barre de progression elle-même (façon ElevenLabs), sans le bouton autour.
 *
 * À poser dans n'importe quel élément `relative overflow-hidden` — un bouton,
 * mais aussi un lien — qui GARDE son propre fond : la barre est un calque,
 * elle ne remplace pas le décor de l'élément (dégradé, image, thème sombre),
 * elle le recouvre seulement sur la portion déjà parcourue.
 *
 * Le contenu passé ici est le MÊME que celui de l'élément hôte : posé par
 * dessus et découpé au bord exact de la barre via `clip-path`, il fait basculer
 * le texte de couleur au pixel près, pile là où la barre le traverse. D'où la
 * règle : `className` doit reproduire EXACTEMENT le layout de l'hôte (flex,
 * gap, padding, taille/graisse de police), sinon le libellé se décale.
 *
 * @param {boolean} loading   - action en cours → barre simulée
 * @param {number}  [progress]- 0..100 pour un mode déterminé (upload…)
 * @param {string}  [fillBg]  - couleur de remplissage de la barre
 * @param {string}  [fillText]- couleur du texte au-dessus de la barre
 * @param {string}  className - classes de layout recopiées de l'hôte
 */
export default function ProgressFill({
    loading = false,
    progress,
    fillBg = "#B23C10",
    fillText = "#ffffff",
    className = "",
    children,
}) {
    const determinate = typeof progress === "number";
    const sim = useSimulatedProgress(!determinate && loading);

    let mounted;
    let pct;
    let visible;
    let barEnabled;
    if (determinate) {
        pct = Math.max(0, Math.min(100, progress));
        mounted = pct > 0;
        visible = true;
        barEnabled = true;
    } else {
        ({ mounted, pct, visible, enabled: barEnabled } = sim);
    }

    // Repli reduced-motion : pas d'animation → remplissage plein statique tant
    // que l'action est en cours, pour garder un feedback clair et sans mouvement.
    const staticFill = !determinate && loading && !barEnabled;
    if (!mounted && !staticFill) return null;

    const overlayPct = staticFill ? 100 : pct;
    const overlayVisible = staticFill ? true : visible;

    return (
        <span
            aria-hidden="true"
            className={`absolute inset-0 ${className}`}
            style={{
                background: fillBg,
                color: fillText,
                opacity: overlayVisible ? 1 : 0,
                transition: staticFill ? "none" : "opacity 0.35s ease",
                clipPath: `inset(0 ${100 - overlayPct}% 0 0)`,
                WebkitClipPath: `inset(0 ${100 - overlayPct}% 0 0)`,
            }}
        >
            {children}
        </span>
    );
}
